<template>
  <div>
    <DatatableReportV3
      id="tags-table"
      ref="tags_table"
      :source="source"
      :config="config"
      :filters="filtersCopy"
      no-download
    >
      <template #actions>
        <SeraButton
          icon="fa fa-plus fa-fw"
          text="New Tag"
          @click="handleAddNewClick"
        />
      </template>
    </DatatableReportV3>

    <TagModal
      v-if="showTagModal"
      :tag="newTag"
      @hide-modal="toggleNewTagModal"
      @save-new-success="handleSaveNewSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";
import { EventBus } from "@/lib/event_bus.js";

import SeraButton from "@/components/Button.vue";
import DatatableReportV3 from "@/Reports/DatatableReportV3.vue";
import TagPill from "@/Tags/TagPill.vue";
import { Tag } from "@/typings/tag";
import TagActionsMenu from "./TagActionsMenu.vue";
import TagModal from "./TagModal.vue";

const props = defineProps<{
  source: string;
  filters: any[];
  taggableType: string;
}>();

const config = {
  responsive: true,
  order: [[0, "desc"]],
  columns: [
    { data: "label", title: "Tag Label", responsivePriority: 1 },
    {
      data: "description",
      title: "Description",
      orderable: false,
      responsivePriority: 3
    },
    {
      data: "usage_count",
      title: "Usage Count",
      searchable: false,
      responsivePriority: 2
    },
    {
      data: "actions",
      title: "",
      sortable: false,
      searchable: false
    }
  ],
  vueComponents: [
    {
      rootId: "tag-actions-menu-vue",
      component: TagActionsMenu
    },
    {
      rootId: "tag-pill-vue",
      component: TagPill
    }
  ]
};

const filtersCopy = ref<any[]>([]);
const showTagModal = ref(false);
const newTag = ref(
  Object.assign(new Tag(), { taggable_type: props.taggableType })
);

// Init filter copy with deep copy of the filters prop.
filtersCopy.value = JSON.parse(JSON.stringify(props.filters));

const removeTagFilter = (tagToRemove: Tag) => {
  EventBus.$emit("clear-datatable-filters");
  filtersCopy.value = filtersCopy.value.map((filter) => {
    if (
      filter.name === "label" &&
      filter.type === "select" &&
      Array.isArray(filter.options)
    ) {
      return {
        ...filter,
        options: filter.options.filter(
          (option: any) => option.value !== tagToRemove.id
        )
      };
    }
    return filter;
  });
  EventBus.$emit("update-tag-filters", props.taggableType, filtersCopy.value);
};

const addTagFilter = (tagToAdd: Tag) => {
  EventBus.$emit("clear-datatable-filters");
  filtersCopy.value = filtersCopy.value.map((filter) => {
    if (
      filter.name === "label" &&
      filter.type === "select" &&
      Array.isArray(filter.options)
    ) {
      // Add the new option if it doesn't already exist
      const optionExists = filter.options.some(
        (option: any) => option.value === tagToAdd.id
      );
      if (!optionExists) {
        return {
          ...filter,
          options: [
            ...filter.options,
            { value: tagToAdd.id, label: tagToAdd.label }
          ]
        };
      }
    }
    return filter;
  });

  EventBus.$emit("update-tag-filters", props.taggableType, filtersCopy.value);
};

const handleAddNewClick = () => {
  if (!showTagModal.value) {
    toggleNewTagModal();
  }
};

const refreshTable = () => {
  EventBus.$emit("refresh-datatable");
};

const toggleNewTagModal = () => {
  showTagModal.value = !showTagModal.value;
};

const handleSaveNewSuccess = (tag) => {
  toggleNewTagModal();
  EventBus.$emit("add-tag-filter", tag);
  refreshTable();
};

onMounted(() => {
  EventBus.$on("remove-tag-filter", removeTagFilter);
  EventBus.$on("add-tag-filter", addTagFilter);
});

onBeforeUnmount(() => {
  EventBus.$off("remove-tag-filter", removeTagFilter);
  EventBus.$off("add-tag-filter", addTagFilter);
});
</script>

<style lang="scss" scoped></style>
